import {
  CreateEnrollmentDTO,
  EnrollmentAuditLogDTO,
  GetEnrollmentsByIdsDTO,
  GetEnrollmentsDTO,
  GetHasRedundantEnrollmentsDTO,
  GetMentorEnrollmentsDetailsDTO,
  GetMentorTeachingAndEnrolledInCoursesDTO,
  GetSessionDetailsDTO,
  GetSessionDTO,
  GetStudentEnrollmentsDetailsDTO,
  GetStudentEnrollmentsDTO,
  PastStudentDTO,
  GetUrgentStudentDataDTO,
  SearchEnrollmentsDTO,
  SearchMentorEnrollmentsDTO,
  SearchParentSessionFeedbackDTO,
  SearchSessionHistoryDTO,
  SearchSessionsDTO,
} from '@shared/clientTypes/DTOs/EnrollmentsDTO';
import { EnrollmentStatus, ISurvey } from '@shared/serverTypes';
import { ApiDescription, ApiData, RequestsMethod } from '../clientTypes';
import {
  Enrollment,
  SearchMentorEnrollmentsParams,
  ActiveStudents,
  EnrollmentStatusTypes,
  TriState,
  SurveyType,
  SurveyFrom,
} from '../common';

export const getEnrollmentsByIds: ApiDescription = {
  endpoint: '/enrollments/by-ids',
  method: RequestsMethod.Get,
};
export type GetEnrollmentsByIds = ApiData<
  {
    page?: number;
    limit?: number;
    enrollmentsIds: string;
  },
  GetEnrollmentsByIdsDTO
>;

export const getEnrollment: ApiDescription = {
  endpoint: '/enrollments/:id',
  method: RequestsMethod.Get,
};
export type GetEnrollment = ApiData<never, Enrollment>;

export const createEnrollment: ApiDescription = {
  endpoint: '/enrollments',
  method: RequestsMethod.Post,
};
export type CreateEnrollment = ApiData<
  Partial<Omit<Enrollment, 'mentor'>> & {
    userId: string;
    courseId: string;
    numberOfSessions: number;
    mentorId?: string;
    initialComment?: string;
  },
  CreateEnrollmentDTO
>;

export const updateEnrollment: ApiDescription = {
  endpoint: '/enrollments/update',
  method: RequestsMethod.Patch,
};
export type UpdateEnrollment = ApiData<
  Partial<Omit<Enrollment, 'sessions'>> & {
    enrollmentId: string;
    mentorId?: string;
    commentText?: string;
  },
  { mailSent: boolean }
>;

export const changeEnrollmentStatus: ApiDescription = {
  endpoint: '/enrollments/update/change-status/:id',
  method: RequestsMethod.Patch,
};
export type ChangeEnrollmentStatus = ApiData<
  {
    newStatus?: EnrollmentStatusTypes;
    reason?: string;
  },
  Enrollment
>;

export const changeEnrollmentsStatus: ApiDescription = {
  endpoint: '/enrollments/update/change-status-for-many',
  method: RequestsMethod.Patch,
};
export type ChangeEnrollmentsStatus = ApiData<
  {
    enrollmentIds: string[];
    newStatus?: EnrollmentStatusTypes;
    reason?: string;
  },
  Enrollment[]
>;

export const getEnrollments: ApiDescription = {
  endpoint: '/user-enrollments/get-enrollments/:id',
  method: RequestsMethod.Get,
};
export type GetEnrollments = ApiData<{ id: string }, GetEnrollmentsDTO>;

export const getUserEnrollments: ApiDescription = {
  endpoint: '/enrollments/user/:id',
  method: RequestsMethod.Get,
};
export type GetUserEnrollments = ApiData<never, Enrollment[]>;

export const getStudentEnrollments: ApiDescription = {
  endpoint: '/enrollments/student',
  method: RequestsMethod.Get,
};
export type GetStudentEnrollments = ApiData<never, GetStudentEnrollmentsDTO>;

export const searchMentorEnrollments: ApiDescription = {
  endpoint: '/enrollments/search-mentor-enrollments',
  method: RequestsMethod.Get,
};

export type SearchMentorEnrollments = ApiData<
  SearchMentorEnrollmentsParams,
  SearchMentorEnrollmentsDTO
>;

export const getActiveStudentsByMentor: ApiDescription = {
  endpoint: '/enrollments/search-active-students',
  method: RequestsMethod.Get,
};

export type GetActiveStudentsByMentor = ApiData<unknown, ActiveStudents[]>;

export const getPastStudentsByMentor: ApiDescription = {
  endpoint: '/enrollments/past-students',
  method: RequestsMethod.Get,
};

export type GetPastStudentsByMentor = ApiData<unknown, PastStudentDTO[]>;

export const endEnrollment: ApiDescription = {
  endpoint: '/enrollments/end',
  method: RequestsMethod.Post,
};

export type EndEnrollment = ApiData<{ enrollmentId: string }, Enrollment>;

export const resumeEnrollment: ApiDescription = {
  endpoint: '/enrollments/resume',
  method: RequestsMethod.Post,
};

export type ResumeEnrollment = ApiData<{ enrollmentId: string }, Enrollment>;

export const addSession: ApiDescription = {
  endpoint: '/enrollments/add-session',
  method: RequestsMethod.Patch,
};
export type AddSession = ApiData<{ enrollmentId: string; isExtraSession?: boolean }, Enrollment>;

export const deleteSession: ApiDescription = {
  endpoint: '/enrollments/delete-session',
  method: RequestsMethod.Patch,
};
export type DeleteSession = ApiData<{ enrollmentId: string; sessionId: string }, Enrollment>;

export const assignSessionsMentor: ApiDescription = {
  endpoint: '/enrollments/assign-sessions-mentor',
  method: RequestsMethod.Patch,
};
export type AssignSessionsMentor = ApiData<
  {
    enrollmentId: string;
    sessionIds: string[];
    mentorId: string | null;
  },
  Enrollment
>;

export const getMentorEnrollmentsDetails: ApiDescription = {
  endpoint: '/enrollments/mentor/:id/details',
  method: RequestsMethod.Get,
};
export type GetMentorEnrollmentsDetails = ApiData<
  {
    id: string;
    teachingPage: number;
    beingTaughtPage: number;
    isEnded: boolean;
    limit?: number;
  },
  GetMentorEnrollmentsDetailsDTO
>;

export const getStudentEnrollmentsDetails: ApiDescription = {
  endpoint: '/enrollments/student/:id/details',
  method: RequestsMethod.Get,
};

export type GetStudentEnrollmentsDetails = ApiData<
  {
    id: string;
    page: number;
    isEnded: boolean;
    limit?: number;
  },
  GetStudentEnrollmentsDetailsDTO
>;

export const getMentorTeachingAndEnrolledInCourses: ApiDescription = {
  endpoint: '/enrollments/mentor/:id/teaching-and-enrolled-in',
  method: RequestsMethod.Get,
};

export type GetMentorTeachingAndEnrolledInCourses = ApiData<
  {
    id: string;
  },
  GetMentorTeachingAndEnrolledInCoursesDTO
>;

export const getMinimumMentorCapacity: ApiDescription = {
  endpoint: '/v2/enrollments/minimum-mentor-capacity',
  method: RequestsMethod.Get,
};

export type GetMinimumMentorCapacity = ApiData<
  never,
  { currentCapacity: number; activeEnrollments: number }
>;

export const hasMentorUpcomingStudents: ApiDescription = {
  endpoint: '/v2/enrollments/has-upcoming-students',
  method: RequestsMethod.Get,
};

export type HasMentorUpcomingStudents = ApiData<never, boolean>;

export const unenrollUser: ApiDescription = {
  endpoint: '/v2/enrollments/unenroll',
  method: RequestsMethod.Post,
};

export type UnenrollUser = ApiData<{ enrollmentId: string }, unknown>;

export const searchSessions: ApiDescription = {
  endpoint: '/v2/enrollments/search-sessions',
  method: RequestsMethod.Get,
};

export type SearchSessions = ApiData<
  {
    page?: number;
    limit?: number;
    sortOrder?: 'asc' | 'desc';
    sortBy?: string;
    status?: number[];
    course?: string;
    student?: string;
    mentor?: string;
    rangeStart?: string;
    rangeEnd?: string;
    hideNoMeetingSessions?: string;
    systemType?: string[];
    problems?: SessionProblemFilter[];
  },
  SearchSessionsDTO
>;

export type SessionProblemFilter =
  | 'short_billable'
  | 'no_billable'
  | 'pending_review'
  | 'unpayable'
  | 'mentor_comment';

export const searchSessionHistory: ApiDescription = {
  endpoint: '/v2/enrollments/search-session-history',
  method: RequestsMethod.Get,
};

export type SearchSessionHistory = ApiData<
  {
    page?: number;
    limit?: number;
    sortOrder?: 'asc' | 'desc';
    student: string;
    sortBy?: string;
    status?: number;
    course?: string;
  },
  SearchSessionHistoryDTO
>;

export const markSessionAsCompleted: ApiDescription = {
  endpoint: '/v2/enrollments/mark-session-completed',
  method: RequestsMethod.Post,
};

export type MarkSessionAsCompleted = ApiData<
  {
    sessionId: string;
  },
  unknown
>;

export const getSession: ApiDescription = {
  endpoint: '/v2/enrollments/get-session/:session',
  method: RequestsMethod.Get,
};

export type GetSession = ApiData<unknown, GetSessionDTO>;

export const retrySessionAiFeedback: ApiDescription = {
  endpoint: '/v2/enrollments/sessions/:sessionId/retry-ai-feedback',
  method: RequestsMethod.Post,
};

export type RetrySessionAiFeedback = ApiData<
  never,
  { queued: boolean; source: 'recorder' | 'zoom'; meetingId: string }
>;

export const updateSession: ApiDescription = {
  endpoint: '/v2/enrollments/update-session/:sessionId',
  method: RequestsMethod.Patch,
};

export type UpdateSession = ApiData<{ status: number }, unknown>;

export const getSessionDetails: ApiDescription = {
  endpoint: '/v2/enrollments/get-session-details/:session',
  method: RequestsMethod.Get,
};

export type GetSessionDetails = ApiData<unknown, GetSessionDetailsDTO>;

export const getHasRedundantEnrollments: ApiDescription = {
  endpoint: '/v2/enrollments/get-redundant-enrollments',
  method: RequestsMethod.Get,
};

export type GetHasRedundantEnrollments = ApiData<unknown, GetHasRedundantEnrollmentsDTO>;

export const searchEnrollments: ApiDescription = {
  endpoint: '/enrollments/search-enrollments',
  method: RequestsMethod.Get,
};

export type SearchEnrollments = ApiData<
  {
    page?: number;
    limit?: number;
    startDateRangeStart?: string;
    startDateRangeEnd?: string;
    endDateRangeStart?: string;
    endDateRangeEnd?: string;
    course?: string;
    student?: string;
    mentor?: string;
    stage?: string[];
    status?: string[];
    urgency?: 'now' | 'soon';
    sortBy: string | null;
    sortOrder: 'asc' | 'desc' | null;
    graduationYears?: string;
    completedSessionsMin?: number;
    completedSessionsMax?: number;
    hideEnrollmentsWithNoSessions?: TriState;
    showMentorsAsStudents?: TriState;
    showDeactivatedStudents?: TriState;
  },
  SearchEnrollmentsDTO
>;

export const searchParentSurvey: ApiDescription = {
  endpoint: '/v2/enrollments/search-parent-survey',
  method: RequestsMethod.Get,
};

export type SearchParentSurvey = ApiData<
  {
    page?: number;
    limit?: number;
    course?: string;
    student?: string;
    mentor?: string;
    parent?: string;

    sortBy: string | null;
    sortOrder: 'asc' | 'desc' | null;
  },
  any
>;

export const searchParentSessionFeedback: ApiDescription = {
  endpoint: '/v2/enrollments/search-parent-session-feedback',
  method: RequestsMethod.Get,
};

export type SearchParentSessionFeedback = ApiData<
  {
    page?: number;
    limit?: number;
    course?: string;
    student?: string;
    mentor?: string;
    parent?: string;
    startDate?: string;
    endDate?: string;
    sortBy: string | null;
    sortOrder: 'asc' | 'desc' | null;
  },
  SearchParentSessionFeedbackDTO
>;

export const getUrgentStudentData: ApiDescription = {
  endpoint: '/v2/enrollments/get-urgent-student-data',
  method: RequestsMethod.Get,
};
export type GetUrgentStudentData = ApiData<unknown, GetUrgentStudentDataDTO>;

export const getEnrollmentStatusHistory: ApiDescription = {
  endpoint: '/enrollments/:id/status-history',
  method: RequestsMethod.Get,
};
export type GetEnrollmentStatusHistory = ApiData<never, EnrollmentStatus[]>;

export const getEnrollmentAuditLog: ApiDescription = {
  endpoint: '/enrollments/:id/audit-log',
  method: RequestsMethod.Get,
};
export type GetEnrollmentAuditLog = ApiData<never, EnrollmentAuditLogDTO[]>;

export const registerStudentSurvey: ApiDescription = {
  endpoint: '/enrollments/:id/register-student-survey',
  method: RequestsMethod.Post,
};
export type RegisterStudentSurvey = ApiData<unknown, unknown>;

export const registerParentSurvey: ApiDescription = {
  endpoint: '/enrollments/:id/register-parent-survey',
  method: RequestsMethod.Post,
};
export type RegisterParentSurvey = ApiData<
  {
    userId: string;
    token: string;
    enrollmentId: string;
    from: SurveyFrom;
    type: SurveyType;
    questions: any;
  },
  unknown
>;
